import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppSelector } from '@/hooks/useRedux';
import newsletterBackground from '@/assets/images/newsletter.jpg';
import newsletterBackgroundWebp from '@/assets/images/newsletter_webp.webp';
import newsletterMobileBackground from '@/assets/images/newsletter-mobile.jpg';
import newsletterMobileBackgroundWebp from '@/assets/images/newsletter-mobile_webp.webp';
import getSelectedPlan from '@/utils/getSelectedPlan';
import Picture from '@/components/globals/picture/Picture';
import InputText from './inputText/InputText';
import validateEmail from '@/utils/validateEmail';
import handleError from '@/utils/handleError';
import APIKit from '@/utils/APIKit';
import styles from './Newsletter.module.scss';

const Newsletter = () => {
    const plan = useAppSelector((state) => state.plan.currentPlan);
    const { isAloyPlan } = getSelectedPlan(plan);
    const { t, i18n } = useTranslation();
    const [email, setEmail] = useState<string>('');
    const [emailError, setEmailErrors] = useState<string>('');
    const [successfulMessage, setSuccessfulMessage] = useState<string>('');
    const lang = i18n.language;

    const subscribe = () => {
        APIKit.post('/web/newsletter_subscription/', {
            email,
            newsletter_translation: `flykube-${lang}`,
        })
            .then((_res: any) => {
                registerCampaignEmail(email);
                sendWelcomeEmail(email);
                setEmail('');
                setEmailErrors('');
                setSuccessfulMessage(t('successfullySubscribed'));
            })
            .catch((error: any) => {
                console.log('error', { error });
                const errorMessage = handleError(error);
                setSuccessfulMessage('');
                setEmailErrors(errorMessage);
            });
    };

    const registerCampaignEmail = (email: string) => {
        APIKit.post(`/web/campaigns/emails/`, {
            email,
            campaign: import.meta.env.VITE_APP_ENV === 'prod' ? 1 : 2,
            language: lang,
        }).catch((error: any) => {
            console.log('error', { error });
        });
    };

    const sendWelcomeEmail = (email: string) => {
        APIKit.post(`/web/campaigns/emails/welcome-discount/${email}/${lang}/`).catch((error: any) => {
            console.log('error', { error });
        });
    };

    const handleSubscription = () => {
        setEmailErrors('');
        const emailErrorChecking = validateEmail(email);

        if (!emailErrorChecking.isValid) setEmailErrors(emailErrorChecking.errors.email);
        else setEmailErrors('');
        if (emailErrorChecking.isValid) subscribe();
    };

    return (
        <section className={styles.newsletterContainer}>
            <Picture
                desktopImg={newsletterBackground}
                desktopWebp={newsletterBackgroundWebp}
                mobileImg={newsletterMobileBackground}
                mobileWebp={newsletterMobileBackgroundWebp}
                className={styles.backgroundImage}
                lazyLoad
            />
            <div className={styles.innerNewsletterContainer}>
                <div className={styles.formBox}>
                    <h2 className={styles.boxTitle}>
                        <Trans i18nKey="newsletterTitle">
                            Join our
                            <span className={styles.coloured}>Newsletter</span>
                        </Trans>
                    </h2>
                    <h3 className={styles.boxSubtitle}>{t('newsletterBoxTitle')}</h3>
                    <p className={styles.boxDescription}>{t('newsletterBoxText')}</p>
                    <div className={styles.inputWrapper}>
                        <InputText type="email" placeholderText={t('email')} value={email} setValue={setEmail} />
                        {emailError !== '' && <span className={styles.error}>{t(`${emailError}`)}</span>}
                        {successfulMessage !== '' && <span className={styles.correctMessage}>{successfulMessage}</span>}
                    </div>
                    <button onClick={handleSubscription} className={`${styles.btn} ${isAloyPlan && styles.aloy}`}>
                        {t('letsGo')}
                    </button>
                </div>
            </div>
        </section>
    );
};

export default Newsletter;
